/**
 * Push Preferences Card Component
 * Push notification toggle with mobile app info
 */

import { Smartphone, Download, Loader2, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';

interface PushPreferencesCardProps {
  enabled: boolean;
  isLoading: boolean;
  onToggle: (enabled: boolean) => void;
}

export function PushPreferencesCard({
  enabled,
  isLoading,
  onToggle,
}: PushPreferencesCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Smartphone className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">
              {t('notifications.push.title', 'Push Notifications')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('notifications.push.subtitle', 'Real-time alerts on your device')}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Toggle row */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">
              {t('notifications.push.enable', 'Enable Push Notifications')}
            </div>
            <div className="text-sm text-gray-500">
              {t('notifications.push.enableDescription', 'Get instant alerts when watched items go on sale')}
            </div>
          </div>
          <button
            onClick={() => onToggle(!enabled)}
            disabled={isLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              enabled ? 'bg-primary-600' : 'bg-gray-200'
            }`}
          >
            {isLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              </span>
            ) : (
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            )}
          </button>
        </div>

        {/* Mobile app info box */}
        <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
          <div className="flex items-start gap-3">
            <Download className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-indigo-800">
                {t('notifications.push.mobileApp', 'Get the Rumio mobile app')}
              </div>
              <p className="text-sm text-indigo-700 mt-1">
                {t(
                  'notifications.push.mobileAppDescription',
                  'Download our mobile app for the best notification experience with instant alerts.'
                )}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href="https://apps.apple.com/app/rumio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {t('notifications.push.appStore', 'App Store')}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.rumio.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {t('notifications.push.googlePlay', 'Google Play')}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
